import { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";

// Layout
import AppShell from "./components/AppShell";

// Pages
import LoginPage from "./pages/LoginPage";
import Dashboard from "./pages/Dashboard";
import ProposalEditor from "./pages/ProposalEditor";
import PublicProposal from "./pages/PublicProposal";
import AddOnsPage from "./pages/AddOnsPage";
import BenefitsPage from "./pages/BenefitsPage";
import TemplatesPage from "./pages/TemplatesPage";
import PackagesPage from "./pages/PackagesPage";

// CRM Pages
import ContactsPage from "./pages/ContactsPage";
import ContactDetailPage from "./pages/ContactDetailPage";
import CompaniesPage from "./pages/CompaniesPage";
import CompanyDetailPage from "./pages/CompanyDetailPage";
import DealsPage from "./pages/DealsPage";
import DealDetailPage from "./pages/DealDetailPage";
import PipelinePage from "./pages/PipelinePage";
import TodayPage from "./pages/TodayPage";
import RegionSlotsPage from "./pages/RegionSlotsPage";
import UsersPage from "./pages/UsersPage";
import DatabasePage from "./pages/DatabasePage";
import ImportsPage from "./pages/ImportsPage";
import ImportDetailPage from "./pages/ImportDetailPage";
import NeituriProposal from "./pages/NeituriProposal";
import PublicProposalV2 from "./pages/PublicProposalV2";
import ProposalEditorV2 from "./pages/ProposalEditorV2";
import ProposalsListPage from "./pages/ProposalsListPage";

/* Smart proposal router: sections-based → V2, legacy → old */
function isValidV2Sections(sections) {
  if (!sections || typeof sections !== "object") return false;
  if (Object.keys(sections).length === 0) return false;
  const hasNewKeys = !!sections.membership;
  const hasOldKeys = ["foundation", "story_activation", "onsite"].every(k => !!sections[k]);
  return hasNewKeys || hasOldKeys;
}

function ProposalRouter() {
  const { proposalId } = require("react-router-dom").useParams();
  const [renderer, setRenderer] = useState(null); // "v2" | "v1"
  useEffect(() => {
    (async () => {
      try {
        let savedPw = "";
        try { savedPw = localStorage.getItem(`sp_pw_${proposalId}`) || ""; } catch (e) {}
        const url = savedPw ? `${API}/public/proposals/${proposalId}?password=${encodeURIComponent(savedPw)}` : `${API}/public/proposals/${proposalId}`;
        const { data } = await axios.get(url);
        // Locked proposals always route to V2 — V2 renders its own password gate.
        if (data?.requires_password) {
          console.log(`[ProposalRouter] id=${proposalId} → V2 (locked, gate)`);
          setRenderer("v2");
          return;
        }
        const useV2 = isValidV2Sections(data.sections);
        console.log(`[ProposalRouter] id=${proposalId} sections=${JSON.stringify(data.sections ? Object.keys(data.sections) : null)} → ${useV2 ? "V2" : "V1"}`);
        setRenderer(useV2 ? "v2" : "v1");
      } catch (err) {
        console.error(`[ProposalRouter] id=${proposalId} fetch failed:`, err);
        setRenderer("v1");
      }
    })();
  }, [proposalId]);
  if (!renderer) return <div className="min-h-screen bg-[#030303] flex items-center justify-center"><div className="w-8 h-8 border-2 border-[#C9A96E]/30 border-t-[#C9A96E] rounded-full animate-spin" /></div>;
  return renderer === "v2" ? <PublicProposalV2 /> : <PublicProposal />;
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
export const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// Language Context
export const LanguageContext = createContext(null);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within LanguageProvider");
  }
  return context;
};

// Auth Provider
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
        } catch (error) {
          localStorage.removeItem("token");
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };
    checkAuth();
  }, [token]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem("token", access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const register = async (email, password, name) => {
    await axios.post(`${API}/auth/register`, { email, password, name });
    return login(email, password);
  };

  const logout = () => {
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, login, register, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

// Language Provider with multi-language support
const LANGUAGES = {
  en: { name: "English", flag: "\u{1F1EC}\u{1F1E7}" },
  de: { name: "Deutsch", flag: "\u{1F1E9}\u{1F1EA}" },
  fr: { name: "Fran\u00e7ais", flag: "\u{1F1EB}\u{1F1F7}" },
  es: { name: "Espa\u00f1ol", flag: "\u{1F1EA}\u{1F1F8}" },
  it: { name: "Italiano", flag: "\u{1F1EE}\u{1F1F9}" },
  th: { name: "\u0E44\u0E17\u0E22", flag: "\u{1F1F9}\u{1F1ED}" }
};

const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState("en");
  const [availableLanguages, setAvailableLanguages] = useState(["en", "de"]);

  const setProposalLanguages = (detectedLang) => {
    const langs = ["en"];
    if (detectedLang && detectedLang !== "en") {
      langs.push(detectedLang);
    } else {
      langs.push("de");
    }
    setAvailableLanguages(langs);
    if (detectedLang && detectedLang !== "en") {
      setLanguage(detectedLang);
    }
  };

  const toggleLanguage = () => {
    const currentIndex = availableLanguages.indexOf(language);
    const nextIndex = (currentIndex + 1) % availableLanguages.length;
    setLanguage(availableLanguages[nextIndex]);
  };

  const t = (enText, translations = {}) => {
    if (language === "en") return enText;
    return translations[language] || translations.de || enText;
  };

  return (
    <LanguageContext.Provider value={{ 
      language, 
      setLanguage, 
      toggleLanguage, 
      t, 
      availableLanguages,
      setAvailableLanguages,
      setProposalLanguages,
      LANGUAGES 
    }}>
      {children}
    </LanguageContext.Provider>
  );
};

// Protected Route with AppShell layout
const ProtectedLayout = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return (
    <AppShell>
      <Outlet />
    </AppShell>
  );
};

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <LanguageProvider>
          <BrowserRouter>
            <Routes>
              {/* Public Routes */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/p/:proposalId" element={<ProposalRouter />} />
              <Route path="/proposal/neituri" element={<NeituriProposal />} />

              {/* Protected Routes - wrapped in AppShell */}
              <Route element={<ProtectedLayout />}>
                <Route path="/pipeline" element={<PipelinePage />} />
                <Route path="/database" element={<DatabasePage />} />
                <Route path="/imports" element={<ImportsPage />} />
                <Route path="/imports/:id" element={<ImportDetailPage />} />
                <Route path="/today" element={<TodayPage />} />
                <Route path="/users" element={<UsersPage />} />
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/" element={<Navigate to="/pipeline" replace />} />
                <Route path="/proposals" element={<ProposalsListPage />} />
                <Route path="/proposals/new" element={<ProposalEditor />} />
                <Route path="/proposals/:id/edit" element={<ProposalEditor />} />
                <Route path="/proposals/:id/sections" element={<ProposalEditorV2 />} />
                <Route path="/addons" element={<AddOnsPage />} />
                <Route path="/benefits" element={<BenefitsPage />} />
                <Route path="/region-slots" element={<RegionSlotsPage />} />
                <Route path="/templates" element={<TemplatesPage />} />
                <Route path="/packages" element={<PackagesPage />} />
                {/* Data Routes */}
                <Route path="/contacts" element={<ContactsPage />} />
                <Route path="/contacts/:id" element={<ContactDetailPage />} />
                <Route path="/companies" element={<CompaniesPage />} />
                <Route path="/companies/:id" element={<CompanyDetailPage />} />
                <Route path="/deals" element={<DealsPage />} />
                <Route path="/deals/:id" element={<DealDetailPage />} />
              </Route>

              {/* Redirect */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
          <Toaster 
            position="top-right" 
            theme="dark"
            toastOptions={{
              style: {
                background: '#0A0A0A',
                border: '1px solid #27272A',
                color: '#FFFFFF'
              }
            }}
          />
        </LanguageProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
